<template>
  <div class="language-selector">
    <button
      class="language-selector__trigger"
      :class="{ 'language-selector__trigger--open': isOpen }"
      type="button"
      @click="handleTriggerClick"
    >
      <span class="trigger__text">{{ BASE_CURRENCY.code }}</span>
      <span class="trigger__sep">·</span>
      <span class="trigger__text">{{ measurementShort }}</span>

      <q-menu
        v-if="!isMobile"
        class="language-selector__menu"
        anchor="bottom right"
        self="top right"
        :offset="[0, 8]"
        @show="menuOpen = true"
        @hide="menuOpen = false"
      >
        <div class="ls-content" @click.stop>
          <div class="ls-section__title">
            {{ $t("header.currency") }}
          </div>
          <span
            v-for="item in selectedCurrencies"
            :key="`curr-${item.code}`"
            class="ls-item"
            :class="{ 'ls-item--active': BASE_CURRENCY.code === item.code }"
            @click="setBaseCurrency(item.code)"
          >
            <span class="ls-item__main ls-item__main--col">
              <p class="ls-item__label">
                {{ item.code }}<span v-if="item.symbol"> · {{ item.symbol }}</span>
              </p>
              <p class="ls-item__sub">
                {{ item.fullName }}
              </p>
            </span>
          </span>

          <div class="ls-section__title ls-section__title--spaced">
            {{ $t("header.measurement") }}
          </div>
          <span
            v-for="item in measureOpts"
            :key="`unit-${item.code}`"
            class="ls-item"
            :class="{ 'ls-item--active': BASE_AREA_UNIT.code === item.code }"
            @click="setBaseAreaUnit(item.code)"
          >
            <p class="ls-item__label ls-item__main">{{ item.title }}</p>
          </span>
        </div>
      </q-menu>
    </button>

    <q-dialog
      v-if="isMobile"
      v-model="sheetOpen"
      position="bottom"
      class="language-selector__dialog"
    >
      <div class="ls-sheet">
        <div class="ls-sheet__handle" />
        <div class="ls-sheet__header">
          <button
            class="ls-sheet__close"
            type="button"
            :aria-label="$t('header.currency')"
            @click="sheetOpen = false"
          >
            <suspense>
              <base-icon
                name="base/Close"
                size="24px"
                color="var(--black-monochrome)"
                filled
              />
            </suspense>
          </button>
        </div>
        <div class="ls-sheet__body">
          <div class="ls-section__title">
            {{ $t("header.currency") }}
          </div>
          <span
            v-for="item in selectedCurrencies"
            :key="`m-curr-${item.code}`"
            class="ls-item ls-item--lg"
            :class="{ 'ls-item--active': BASE_CURRENCY.code === item.code }"
            @click="setBaseCurrency(item.code)"
          >
            <span class="ls-item__main ls-item__main--col">
              <p class="ls-item__label">
                {{ item.code }}<span v-if="item.symbol"> · {{ item.symbol }}</span>
              </p>
              <p class="ls-item__sub">
                {{ item.fullName }}
              </p>
            </span>
          </span>

          <div class="ls-section__title ls-section__title--spaced">
            {{ $t("header.measurement") }}
          </div>
          <span
            v-for="item in measureOpts"
            :key="`m-unit-${item.code}`"
            class="ls-item ls-item--lg"
            :class="{ 'ls-item--active': BASE_AREA_UNIT.code === item.code }"
            @click="setBaseAreaUnit(item.code)"
          >
            <p class="ls-item__label ls-item__main">{{ item.title }}</p>
          </span>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import type { Currency } from "~/types/currencies";
import {
  BASE_CURRENCY,
  CURRENCIES,
  setBaseCurrency,
} from "~/composables/CURRENCIES";
import { BASE_AREA_UNIT, setBaseAreaUnit } from "~/composables/AREA_UNITS";
import { getLocalStorage } from "~/services/LocalStorage/localStorage";

interface NamedCurrency extends Currency {
  fullName: string;
}

const MOBILE_BREAKPOINT = 960;

const { t } = useI18n();
const nuxtApp = useNuxtApp();
const $q = useQuasar();

const menuOpen = ref(false);
const sheetOpen = ref(false);

const isMobile = computed(() => $q.screen.width > 0 && $q.screen.width < MOBILE_BREAKPOINT);

const isOpen = computed(() => menuOpen.value || sheetOpen.value);

const measurementShort = computed(() =>
  BASE_AREA_UNIT.value.code === "ft2" ? "ft²" : "m²"
);

watch(isMobile, (mobile) => {
  if (mobile) menuOpen.value = false;
  else sheetOpen.value = false;
});

const handleTriggerClick = () => {
  if (isMobile.value) sheetOpen.value = !sheetOpen.value;
};

const currencyMeta: Record<string, { symbol: string; fullName: string }> = {
  USD: { symbol: "$", fullName: "United States Dollar" },
  EUR: { symbol: "€", fullName: "Euro" },
  IDR: { symbol: "Rp", fullName: "Indonesian Rupiah" },
};

const currencyOpts = ["USD", "EUR", "IDR"];

const selectedCurrencies = computed(() => {
  return currencyOpts.map((key) => {
    const meta = currencyMeta[key];
    const live = CURRENCIES[key];
    return {
      code: key,
      symbol: live?.symbol || meta.symbol,
      rate: live?.rate ?? 1,
      usdRate: live?.usdRate ?? 1,
      fullName: meta.fullName,
    } as NamedCurrency;
  });
});

const measureOpts = computed(() => [
  { title: t("header.meters"), code: "m2" },
  { title: t("header.feet"), code: "ft2" },
]);

const userUnit = getLocalStorage("user_area_unit");

nuxtApp.hook("page:finish", () => {
  if (userUnit) {
    setBaseAreaUnit(userUnit);
  }
});
</script>

<style lang="scss">
.language-selector {
  position: relative;
  display: inline-flex;

  &__trigger {
    @include drop-button-styles;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border-radius: 20px;
    border: 1px solid transparent;
    background: var(--white-monochrome, #ffffff);
    transition:
      background-color 0.2s ease,
      border-color 0.2s ease;

    &:hover {
      background-color: var(--gray-monochrome-40, rgba(158, 167, 185, 0.4));
    }

    &--open {
      background-color: var(--gray-monochrome-40, rgba(158, 167, 185, 0.4));
    }

    .trigger__sep {
      color: var(--black-monochrome-60, rgba(0, 0, 0, 0.4));
      font-family: "DM Sans", sans-serif;
      font-size: 13px;
      line-height: 1;
      user-select: none;
    }

    .trigger__text {
      font-family: "DM Sans", sans-serif;
      font-size: 13px;
      font-weight: 500;
      letter-spacing: 0.01em;
      line-height: 1;
      color: var(--black-monochrome);
    }
  }

  &__menu {
    display: flex;
    padding: 16px 20px;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    border-radius: 24px;
    background: var(--white-monochrome, #ffffff);
    box-shadow:
      -4px 12px 28px rgba(18, 18, 18, 0.1),
      4px 12px 28px rgba(18, 18, 18, 0.1),
      0 8px 16px rgba(18, 18, 18, 0.04);
    min-width: 280px;
  }

  &__dialog {
    z-index: 9999999;
  }
}

.ls-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
}

.ls-section__title {
  width: 100%;
  padding: 10px 10px 6px;
  font-family: "DM Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--black-monochrome-60, rgba(0, 0, 0, 0.5));

  &--spaced {
    margin-top: 6px;
    border-top: 1px solid rgba(158, 167, 185, 0.2);
  }
}

.ls-item {
  width: 100%;
  display: flex;
  padding: 8px 10px;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  background-color: transparent;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  cursor: pointer;

  &:hover {
    background-color: var(--white-contrast, #f3f7fe);
  }

  &--active {
    background-color: var(--white-contrast, #f3f7fe);
  }

  &__label {
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.25;
    color: var(--black-monochrome, #0a0a0a);
    margin: 0;
  }

  &__sub {
    font-family: "DM Sans", sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.25;
    color: var(--black-monochrome-60, rgba(0, 0, 0, 0.5));
    margin: 2px 0 0;
  }

  &--active &__label {
    color: var(--violet-main, #5a2fcf);
  }

  &__main {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    &--col {
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  }

  &--lg {
    padding: 12px 10px;
  }
}

.ls-sheet {
  background: var(--white-monochrome, #ffffff);
  border-radius: 20px 20px 0 0;
  width: 100vw;
  max-width: 100vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__handle {
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: var(--gray-monochrome-40, rgba(158, 167, 185, 0.4));
    margin: 8px auto 4px;
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px 12px;
  }

  &__close {
    @include drop-button-styles;
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--gray-monochrome-40, rgba(158, 167, 185, 0.4));
    }
  }

  &__body {
    padding: 8px 20px 32px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
